import React, { useId } from 'react';
import { motion } from 'framer-motion';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
interface RainBandProps {
  hours: {
    time: string;
    mm: number;
  }[];
}
const W = 380;
const H = 92;
const BASE = 80;
const ROW = 18;

/** Rain as a soft band: its height is the intensity, gentle streaks fall inside it. Draws itself in. */
export function RainBand({
  hours
}: RainBandProps) {
  const reduce = useMotionPreference();
  const id = useId().replace(/:/g, '');
  const max = Math.max(1.2, ...hours.map(h => h.mm));
  const n = hours.length;
  const pts = hours.map((h, i) => [n === 1 ? W / 2 : i / (n - 1) * W, BASE - h.mm / max * (BASE - 14)] as const);
  let line = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < n; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const mx = (x0 + x1) / 2;
    line += ` C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`;
  }
  const area = `${line} L ${W} ${BASE} L 0 ${BASE} Z`;
  const cols = Array.from({
    length: 34
  }, (_, i) => i * 12 - 24);
  const rows = Array.from({
    length: Math.ceil(H / ROW) + 2
  }, (_, r) => r * ROW - ROW);
  return <div data-id="element-1554" data-mp="rlk8h9y2e6h6">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block h-[92px] w-full" role="img" aria-label={`Neerslag: ${hours.map(h => `${h.time.slice(0, 2)} uur ${String(h.mm).replace('.', ',')} mm`).join(', ')}`} data-id="element-1555" data-mp="ro6c4yhu41u4">
        <defs data-id="element-1556" data-mp="3vhfmrmbswhi">
          <clipPath id={`rain-${id}`} data-id="element-1557" data-mp="3yvgfvfccxmu">
            <path d={area} data-id="element-1558" data-mp="7yvzjtddxx08" />
          </clipPath>
          <clipPath id={`reveal-${id}`} data-id="element-1559" data-mp="7zp4qpwp4eg9">
            <motion.rect x="0" y="0" height={H} initial={{
            width: reduce ? W : 0
          }} animate={{
            width: W
          }} transition={{
            duration: 0.9,
            ease: [0.65, 0, 0.35, 1]
          }} data-id="element-1560" data-mp="3q4io4z8nh5gs" />
          </clipPath>
        </defs>
        <line x1="0" x2={W} y1={BASE} y2={BASE} stroke="rgba(255,255,255,0.14)" strokeWidth="1" data-id="element-1561" data-mp="1tbgyx1jns2av" />
        <g clipPath={`url(#reveal-${id})`} data-id="element-1562" data-mp="1rua0xa8aw9ps">
          <path d={area} fill="#8FB8D8" fillOpacity="0.22" data-id="element-1563" data-mp="1rw4oyxevchy3" />
          <g clipPath={`url(#rain-${id})`} data-id="element-1564" data-mp="1muxhkzgn1kry">
            <motion.g animate={reduce ? undefined : {
            y: [0, ROW]
          }} transition={{
            duration: 0.9,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-1565" data-mp="1mu5ktlbb1h03">
              {cols.map(x => rows.map(y => <line key={`${x}-${y}`} x1={x + y / ROW * 4} y1={y} x2={x + y / ROW * 4 - 3} y2={y + 9} stroke="#C4DEF0" strokeOpacity="0.6" strokeWidth="1.4" strokeLinecap="round" data-id="element-1566" data-mp="1cz3zz1o2b13b" />))}
            </motion.g>
          </g>
          <path d={line} fill="none" stroke="#8FB8D8" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-1567" data-mp="2tbsp3sv1oipy" />
        </g>
      </svg>
      <div className="mt-1 flex justify-between text-[22px] tabular-nums text-ink-muted" aria-hidden data-id="element-1568" data-mp="2oj9en4rnm3pd">
        {hours.map(h => <span key={h.time} data-id="element-1569" data-mp="2wvt7uq3t4zno">{h.time.slice(0, 2)}</span>)}
      </div>
    </div>;
}